import {useState} from 'react';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import {useDirectConnectNotifications} from '@/components/global/providers/DirectConnectNotificationsProvider';
import {Modal} from '@/components/ui';
import useIdentity from '@/hooks/useIdentity';
import {NAVLINK_TRACKING} from '../appBarTracking';
import useNavbarVersion from '../hooks/useNavbarVersion';
import useNavLinkTracking from '../hooks/useNavlinkTracking';
import MenuPopover from '../Menus/MenuPopover';
import DirectConnectNotification from './DirectConnectNotification';
import NotificationBadge from './NotificationBadge';
import NotificationsButton from './NotificationButton';
import NotificationsOpenTracker from './NotificationsOpenTracker';

const Notifications: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {t} = useTranslation('app-bar');
  const {isAdmin} = useIdentity();
  const notifications = useDirectConnectNotifications();
  const unreadNotificationCount = notifications
    ? notifications?.filter(notification => notification.isUnread).length
    : 0;
  const {trackLink, trackRumAction} = useNavLinkTracking();

  const version = useNavbarVersion();

  const handleNotificationsClick = () => {
    trackRumAction({datadogAction: NAVLINK_TRACKING.notifications.clicked, version});
  };

  const wrapperClassName = twJoin(isAdmin && 'hidden appbar-sm:block');

  if (notifications && notifications?.length > 0) {
    return (
      <div className={wrapperClassName}>
        <div className="hidden appbar-md:block">
          <div className="relative flex items-center justify-start gap-1">
            <MenuPopover
              arrowClasses="right-0.5"
              className="w-[350px] p-0"
              content={
                <>
                  <NotificationsOpenTracker area="desktop" version={version} />
                  <div className="border-b border-b-peppercorn-100 p-4 text-xl font-bold text-peppercorn-800">
                    {t('notifications.title')}
                  </div>
                  {notifications.map(
                    ({brandName, changeType, id, isUnread, message, messageType, orderUuid}) => (
                      <DirectConnectNotification
                        key={id}
                        brandName={brandName}
                        changeType={changeType}
                        isUnread={isUnread}
                        message={message}
                        messageType={messageType}
                        orderUuid={orderUuid}
                        trackLink={trackLink}
                      />
                    ),
                  )}
                </>
              }
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              placement="bottom-end"
            >
              <NotificationsButton
                onClick={handleNotificationsClick}
                unreadNotificationCount={unreadNotificationCount}
              />
            </MenuPopover>
            {unreadNotificationCount > 0 && (
              <NotificationBadge unreadNotificationCount={unreadNotificationCount} />
            )}
          </div>
        </div>

        <div className="appbar-md:hidden">
          <Modal
            cancelLabel={t('notifications.close')}
            slotProps={{content: {className: 'px-2'}}}
            title={t('notifications.title')}
            trigger={
              <div className="relative flex items-center justify-start gap-1">
                <NotificationsButton
                  unreadNotificationCount={unreadNotificationCount}
                  onClick={handleNotificationsClick}
                />
                {unreadNotificationCount > 0 && (
                  <NotificationBadge unreadNotificationCount={unreadNotificationCount} />
                )}
              </div>
            }
          >
            <>
              <NotificationsOpenTracker area="mobile" version={version} />
              {notifications.map(
                ({id, orderUuid, isUnread, brandName, messageType, changeType, message}) => (
                  <DirectConnectNotification
                    key={id}
                    brandName={brandName}
                    changeType={changeType}
                    isUnread={isUnread}
                    message={message}
                    messageType={messageType}
                    orderUuid={orderUuid}
                    trackLink={trackLink}
                  />
                ),
              )}
            </>
          </Modal>
        </div>
      </div>
    );
  }

  return null;
};

export default Notifications;
